import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Logo positions around the circle
const LOGO_POSITIONS = [
  { name: 'top', top: '2%', left: '50%', transform: 'translateX(-50%)' },
  { name: 'top-right', top: '12%', right: '12%' },
  { name: 'right', top: '50%', right: '2%', transform: 'translateY(-50%)' },
  { name: 'bottom-right', bottom: '12%', right: '12%' },
  { name: 'bottom', bottom: '2%', left: '50%', transform: 'translateX(-50%)' },
  { name: 'bottom-left', bottom: '12%', left: '12%' },
  { name: 'left', top: '50%', left: '2%', transform: 'translateY(-50%)' },
  { name: 'top-left', top: '12%', left: '12%' },
];

const Hero: React.FC = () => {
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleLogin = async () => {
    try {
      setGoogleLoading(true);
      const response = await fetch(`${API_URL}/api/auth/google`);
      const data = await response.json();

      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        console.error('No auth URL received');
        alert('Google login is not configured. Please try again later.');
      }
    } catch (error) {
      console.error('Failed to initiate Google login:', error);
      alert('Failed to connect to Google. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] as const },
    },
  };

  const features = [
    'One-click product listings powered by GPT-4o',
    'Smart repricing with Safe Price Protection',
    'Automatic ordering from Amazon to eBay',
    '24/7 stock & price sync across 8 marketplaces',
  ];

  const trustLogos = [
    { name: 'Amazon', image: '/images/logos/Amazon_logo.svg.png', stars: 5 },
    { name: 'eBay', image: '/images/logos/EBay_logo.svg.png', stars: 5 },
    { name: 'Shopify', image: '/images/logos/Shopify_logo_2018.svg.png', stars: 5 },
  ];

  // New round logos
  const platforms = [
    { id: 'amazon', image: '/images/logos/Amazon_icon.svg' },
    { id: 'ebay', image: '/images/logos/ebay-circle-round-logo-19790.svg' },
    { id: 'shopify', image: '/images/logos/shopify-circle-round-logo-19787.svg' },
    { id: 'aliexpress', image: '/images/logos/aliexpress-social.png' },
    { id: 'walmart', image: '/images/logos/new-Walmart-logo-02.png' },
    { id: 'tiktok', image: '/images/logos/vecteezy_tiktok-circle-icon-for-web-design_20964382.png' },
  ];

  // State for visible logos with random positions
  const [visibleLogos, setVisibleLogos] = useState<Array<{ platform: typeof platforms[0], position: typeof LOGO_POSITIONS[0] }>>([]);
  const [usedPositions, setUsedPositions] = useState<Set<string>>(new Set());
  const [platformIndex, setPlatformIndex] = useState(0);

  // Get random available position
  const getRandomPosition = (excludePositions: Set<string>) => {
    const available = LOGO_POSITIONS.filter(p => !excludePositions.has(p.name));
    if (available.length === 0) return LOGO_POSITIONS[Math.floor(Math.random() * LOGO_POSITIONS.length)];
    return available[Math.floor(Math.random() * available.length)];
  };

  // Cycle logos with random positions
  useEffect(() => {
    const interval = setInterval(() => {
      setPlatformIndex(prev => {
        const nextIndex = (prev + 1) % platforms.length;
        const platform = platforms[nextIndex];

        // Get random position not currently in use
        const newUsedPositions = new Set(usedPositions);

        // Remove oldest logo if we have 2
        if (visibleLogos.length >= 2) {
          const oldest = visibleLogos[0];
          newUsedPositions.delete(oldest.position.name);
          setVisibleLogos(prev => {
            const newLogos = prev.slice(1);
            const newPos = getRandomPosition(newUsedPositions);
            newUsedPositions.add(newPos.name);
            return [...newLogos, { platform, position: newPos }];
          });
        } else {
          const newPos = getRandomPosition(newUsedPositions);
          newUsedPositions.add(newPos.name);
          setVisibleLogos(prev => [...prev, { platform, position: newPos }]);
        }

        setUsedPositions(newUsedPositions);
        return nextIndex;
      });
    }, 2500);

    return () => clearInterval(interval);
  }, [visibleLogos, usedPositions]);

  // Initialize with 2 logos
  useEffect(() => {
    const pos1 = LOGO_POSITIONS[1]; // top-right
    const pos2 = LOGO_POSITIONS[5]; // bottom-left
    setVisibleLogos([
      { platform: platforms[0], position: pos1 },
      { platform: platforms[1], position: pos2 },
    ]);
    setUsedPositions(new Set([pos1.name, pos2.name]));
    setPlatformIndex(1);
  }, []);

  // Generate dots - perfectly centered at 50% of container
  const dots = useMemo(() => {
    const result: Array<{ x: number; y: number; size: number; color: string; key: string }> = [];
    const rings = 5;
    const svgSize = 400;
    const center = svgSize / 2; // Exact center = 200

    for (let ring = 1; ring <= rings; ring++) {
      const radius = 45 + ring * 28;
      const numDots = 8 + ring * 4;
      const dotSize = ring <= 2 ? 7 : (ring <= 4 ? 6 : 5);

      for (let i = 0; i < numDots; i++) {
        const angle = (i / numDots) * 2 * Math.PI - Math.PI / 2; // Start from top
        const x = center + radius * Math.cos(angle);
        const y = center + radius * Math.sin(angle);

        // Vivid colors - orange/peach to sage green
        const colorRatio = (x - 50) / 300;
        const hue = 28 + colorRatio * 60;
        const sat = 55 + colorRatio * 20;
        const light = 55 + colorRatio * 10;

        result.push({
          x,
          y,
          size: dotSize,
          color: `hsl(${hue}, ${sat}%, ${light}%)`,
          key: `${ring}-${i}`
        });
      }
    }
    return result;
  }, []);

  return (
    <section className="hero-section" id="hero">
      <div className="hero-bg-gradient" />

      <Container>
        <Row className="align-items-center hero-row">
          {/* Left Content */}
          <Col lg={6} className="hero-content-col">
            <motion.div
              className="hero-content"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.h1 variants={itemVariants} className="hero-headline">
                AI-Powered
                <br />
                <span className="headline-highlight">Automation Platform</span>
              </motion.h1>

              <motion.ul variants={itemVariants} className="hero-features-list">
                {features.map((feature, index) => (
                  <li key={index}>
                    <span className="check-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    <span>{feature}</span>
                  </li>
                ))}
              </motion.ul>

              <motion.div variants={itemVariants} className="hero-cta-buttons">
                <button
                  onClick={handleGoogleLogin}
                  disabled={googleLoading}
                  className="btn-cta-google"
                  style={{ cursor: googleLoading ? 'wait' : 'pointer' }}
                >
                  {googleLoading ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  )}
                  {googleLoading ? 'Connecting...' : 'Start with Google'}
                </button>
                <a href={`${process.env.REACT_APP_APP_URL || ''}/register`} className="btn-cta-primary">
                  Start 14-day Free Trial
                </a>
              </motion.div>

              <motion.div variants={itemVariants} className="hero-trust-logos">
                {trustLogos.map((logo, index) => (
                  <div key={index} className="trust-logo-item">
                    <img src={logo.image} alt={`${logo.name} integration - CodeRom dropshipping platform`} />
                    <div className="trust-stars">
                      {[...Array(logo.stars)].map((_, i) => (
                        <svg key={i} width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                      ))}
                    </div>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </Col>

          {/* Right Visual */}
          <Col lg={6} className="hero-visual-col">
            <div className="hero-visual">
              {/* SVG for rotating dots with center logo inside */}
              <svg className="rotating-dots-svg" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid meet">
                <g className="dots-group">
                  {dots.map((dot) => (
                    <circle
                      key={dot.key}
                      cx={dot.x}
                      cy={dot.y}
                      r={dot.size}
                      fill={dot.color}
                    />
                  ))}
                </g>
              </svg>

              {/* Center Logo */}
              <motion.div
                className="center-logo"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.6, delay: 0.3, type: "spring" }}
              >
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
              </motion.div>

              {/* Orbiting Platform Logos - Random Positions */}
              <AnimatePresence>
                {visibleLogos.map((item, index) => (
                  <motion.div
                    key={`${item.platform.id}-${item.position.name}-${index}`}
                    className="orbit-platform-logo"
                    style={{
                      top: item.position.top,
                      left: item.position.left,
                      right: item.position.right,
                      bottom: item.position.bottom,
                      transform: item.position.transform,
                    }}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                    transition={{
                      duration: 0.6,
                      type: "spring",
                      stiffness: 200,
                      damping: 20
                    }}
                  >
                    <img src={item.platform.image} alt={`${item.platform.id.charAt(0).toUpperCase() + item.platform.id.slice(1)} marketplace integration`} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Hero;
